/**
 * The build: shipped, plus the order book, plus what is still to order.
 *
 * A waterfall rather than three bars from zero, because the three layers stack
 * into one total and the reader's question is how much of the month is already
 * fact. The fourth column is the stored total drawn as an outline, not a filled
 * bar: it is the same quantity as the stack to its left, and filling it would
 * read as a fourth contribution.
 *
 * **Gallons only.** The shipped/committed dollar split is not stored (see the
 * report manifest), so a dollar waterfall could only be drawn by inventing the
 * division. The panel says so rather than showing three bars where one is real.
 *
 * Two comparisons are drawn over the stack: prior year as a reference line, and
 * the measured range the forecast has historically landed in as a shaded band
 * with its midpoint marked. The band is the backtest's answer to "how wrong has
 * this been" and it sits *above* the total on purpose -- the forecast has run
 * low in every band but a closed month, so the honest picture is a total with
 * headroom, not a total with error bars either side of it.
 */

import { useElementWidth } from '../shared/useElementWidth'

import { compact, gallons, NONE } from './format'

export interface BuildStep {
  readonly step: string
  readonly layer: string
  readonly gallons: number | null
  readonly sharePct: number | null
}

interface Props {
  readonly steps: readonly BuildStep[]
  readonly priorYear: number | null
  readonly bandLow: number | null
  readonly bandMid: number | null
  readonly bandHigh: number | null
}

const HEIGHT = 320
const MARGIN = { top: 40, right: 16, bottom: 56, left: 58 }

/** One fill per layer; the total is an outline and takes none. */
const LAYER_FILL: Record<string, string> = {
  shipped: 'var(--seq-600)',
  committed: 'var(--seq-400)',
  predicted: 'var(--seq-200)',
}

export function BuildWaterfall({ steps, priorYear, bandLow, bandMid, bandHigh }: Props) {
  const { ref, width } = useElementWidth<HTMLDivElement>()
  const chartWidth = Math.max(width || 560, 360)

  const drawn = steps.filter((s) => s.gallons !== null)
  if (drawn.length === 0) {
    return <div className="state">No build figures in this run</div>
  }

  const references = [
    { value: priorYear, label: `prior year ${gallons(priorYear)}`, tone: 'ref' as const },
    {
      value: bandMid,
      label: `likely ${gallons(bandMid)}`,
      tone: 'muted' as const,
    },
  ].filter((r) => r.value !== null)

  // Drawn only when the run carried all three: half a band is worse than none.
  const band =
    bandLow !== null && bandHigh !== null && bandHigh > bandLow
      ? { low: bandLow, high: bandHigh }
      : null

  const ceiling = Math.max(
    ...drawn.map((s) => s.gallons ?? 0),
    ...references.map((r) => r.value ?? 0),
    band?.high ?? 0,
  )
  const max = ceiling * 1.08
  const plotHeight = HEIGHT - MARGIN.top - MARGIN.bottom
  const y = (value: number) => MARGIN.top + plotHeight * (1 - value / max)

  const plotWidth = chartWidth - MARGIN.left - MARGIN.right
  const slot = plotWidth / steps.length
  const barWidth = Math.min(120, slot * 0.62)
  const xOf = (index: number) => MARGIN.left + index * slot + (slot - barWidth) / 2

  // Running base, so each layer starts where the previous one ended.
  let running = 0
  const bars = steps.map((step) => {
    const value = step.gallons
    const isTotal = step.layer === 'total'
    const base = isTotal ? 0 : running
    if (!isTotal && value !== null) running += value
    return { step, value, base, isTotal }
  })

  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max)

  return (
    <div className="mef-chart-wrap" ref={ref}>
      <svg
        viewBox={`0 0 ${String(chartWidth)} ${String(HEIGHT)}`}
        width={chartWidth}
        height={HEIGHT}
        role="img"
        aria-label="Waterfall of the month-end build: shipped, plus committed order book, plus expected orders."
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={MARGIN.left}
              x2={chartWidth - MARGIN.right}
              y1={y(tick)}
              y2={y(tick)}
              className="mef-gridline"
            />
            <text x={MARGIN.left - 8} y={y(tick) + 3.5} className="mef-axis" textAnchor="end">
              {compact(tick, 'gal')}
            </text>
          </g>
        ))}

        {band !== null && (
          <rect
            x={MARGIN.left}
            y={y(band.high)}
            width={chartWidth - MARGIN.left - MARGIN.right}
            height={Math.max(2, y(band.low) - y(band.high))}
            className="mef-band"
          />
        )}

        {references.map((reference) => (
          <g key={reference.label}>
            <line
              x1={MARGIN.left}
              x2={chartWidth - MARGIN.right}
              y1={y(reference.value ?? 0)}
              y2={y(reference.value ?? 0)}
              className={`mef-reference mef-reference-${reference.tone}`}
            />
            <text
              x={MARGIN.left + 6}
              y={y(reference.value ?? 0) - 6}
              className={`mef-reference-label mef-reference-label-${reference.tone}`}
            >
              {reference.label}
            </text>
          </g>
        ))}

        {bars.map(({ step, value, base, isTotal }, index) => {
          const x = xOf(index)
          if (value === null) {
            return (
              <text
                key={step.step}
                x={x + barWidth / 2}
                y={y(0) - 8}
                className="mef-axis"
                textAnchor="middle"
              >
                {NONE}
              </text>
            )
          }
          const top = y(base + value)
          const bottom = y(base)
          const height = Math.max(2, bottom - top)
          return (
            <g key={step.step}>
              <rect
                x={x}
                y={top}
                width={barWidth}
                height={height}
                rx={3}
                className={isTotal ? 'mef-bar-total' : 'mef-bar'}
                fill={isTotal ? 'none' : LAYER_FILL[step.layer]}
              />
              <text x={x + barWidth / 2} y={top - 10} className="mef-bar-value" textAnchor="middle">
                {gallons(value)}
              </text>
              <text
                x={x + barWidth / 2}
                y={HEIGHT - MARGIN.bottom + 20}
                className="mef-bar-label"
                textAnchor="middle"
              >
                {step.step}
              </text>
              <text
                x={x + barWidth / 2}
                y={HEIGHT - MARGIN.bottom + 35}
                className="mef-axis"
                textAnchor="middle"
              >
                {step.sharePct === null ? '' : `${step.sharePct.toFixed(1)}%`}
              </text>
            </g>
          )
        })}

        <line
          x1={MARGIN.left}
          x2={chartWidth - MARGIN.right}
          y1={y(0)}
          y2={y(0)}
          className="mef-baseline"
        />
      </svg>
    </div>
  )
}
